import { Brand, CatalogImage } from '@src/types/graphql-types'
import { SectionName } from '@src/components/ProductInfo/SkipToSectionTypes'
import { LegacyRef, ReactNode, RefObject } from 'react'
import { PricingOverrideEventPayload } from '@src/utils/analytics'

export enum CarouselProductTypes {
  RecentlyViewed = 'recently viewed products',
  RecentlyViewedHomepage = 'recently viewed products homepage',
  AlsoViewed = 'customers also viewed',
  CompareItems = 'compare similar items',
  RecommendedProducts = 'recommended products',
  CartRecommendedProducts = 'recommended products', // Used as a special type to trigger certain CSS rendering.
  PopularProducts = 'popular products',
  BestSellers = 'best sellers',
  NewArrivals = 'new arrivals',
  TopDeals = 'top deals',
  FeaturedProducts = 'featured products',
  BuyAgain = 'buy it again',
  RelatedProducts = 'related products',
  HeroCard = 'product hero card',
}

export const CAROUSEL_TITLE_LOOKUP_BY_TYPE = {
  [CarouselProductTypes.RecommendedProducts]: {
    id: 'RECOMMENDED_PRODUCTS',
    defaultMessage: 'Recommended Products',
  },
  [CarouselProductTypes.RelatedProducts]: {
    id: 'RELATED_PRODUCTS',
    defaultMessage: 'Related Products',
  },
  [CarouselProductTypes.RecentlyViewed]: {
    id: 'RECENTLY_VIEWED_PRODUCTS',
    defaultMessage: 'Recently Viewed Products',
  },
  [CarouselProductTypes.AlsoViewed]: {
    id: 'CUSTOMERS_ALSO_VIEWED',
    defaultMessage: 'Customers Also Viewed',
  },
  [CarouselProductTypes.BestSellers]: {
    id: 'SHOP_BEST_SELLERS',
    defaultMessage: 'Shop Best Sellers',
  },
  [CarouselProductTypes.BuyAgain]: {
    id: 'BUY_IT_AGAIN',
    defaultMessage: 'Buy It Again',
  },
  [CarouselProductTypes.NewArrivals]: {
    id: 'NEW_PRODUCT',
    defaultMessage: 'New Product',
  },
  [CarouselProductTypes.PopularProducts]: {
    id: 'POPULAR_PRODUCTS',
    defaultMessage: 'Popular Products',
  },
  [CarouselProductTypes.RecentlyViewedHomepage]: {
    id: 'recently viewed products homepage',
    defaultMessage: 'Recently Viewed Products Homepage',
  },
}

export interface CarouselProduct {
  id?: string | null
  productKey: string
  productNumber: string
  productSku?: string | null
  name: string
  description?: string | null
  gaProductCode?: string | null
  brand: Pick<Brand, 'key' | 'erpKey' | 'name' | 'color'>
  images: Pick<CatalogImage, 'altText' | 'mediumUrl'>[]
  isMarketplace: boolean
  tags?: string | null
  discount?: string | null
  analyticsPosition?: number // Lets us override the index/position of the item that is reported to analytics. This occurs on carousel slices for next/prev navigation.
}

export interface ProductCardProps {
  condensed?: boolean
  displayDiscount?: (discountPercent?: string | null) => ReactNode
  hideProductImg?: boolean
  isProductRecommendation?: boolean
  isRaisedCardLayout?: boolean
  preloadCardImages?: boolean
  products: CarouselProduct[]
  slidesToShow?: number
  title?: JSX.Element | string
  headerRight?: ReactNode
  additionalInfoRenderer?: (product: CarouselProduct) => ReactNode
  cardFooterVariant?: 'quick-view' | 'none' | 'add-to-cart'
  onFooterAction?: (product: CarouselProduct) => Promise<void> | void
  type: CarouselProductTypes
  productHeroCard?: boolean
  updateSkipToLinks?: (sectionName: SectionName, ref: RefObject<null>) => void
  withoutControls?: boolean
  setRef?: (refType: string) => LegacyRef<HTMLDivElement> | undefined

  // Analytics values we allow to be prop drilled down into carousels. Which isn't ideal but... Gotta do what you gotta do.
  analyticsItemListingId?: string | null
  analyticsItemListingName?: string | null
  gaPricingOverrides?: PricingOverrideEventPayload
}
